/* istanbul ignore file */
import { createAction } from '@reduxjs/toolkit';
import type { FinnovaCodeColumn, IIstProduct, IKontodepot, IProduct, IPowerOfAttorney } from '../../api/domain';
import type { ICustomerBase } from '../../api/domain/customer-base';
import type { IAddress, ICommunicationEntries, IEBankingContract, IFinnovaCode, IIstKarte } from '../../api/domain/finnova';

enum FinnovaApiActions {
    FETCH_KANN_PRODUCTS = 'finnova.fetch-kann-products',
    FETCH_KANN_PRODUCTS_SUCCESS = 'finnova.fetch-kann-products.SUCCESS',
    FETCH_KANN_PRODUCTS_FAILURE = 'finnova.fetch-kann-products.FAILURE',

    FETCH_CARD_PRODUCTS = 'finnova.fetch-card-products',
    FETCH_CARD_PRODUCTS_SUCCESS = 'finnova.fetch-card-products.SUCCESS',
    FETCH_CARD_PRODUCTS_FAILURE = 'finnova.fetch-card-products.FAILURE',

    FETCH_IST_PRODUCTS = 'finnova.fetch-ist-products',
    FETCH_IST_PRODUCTS_SUCCESS = 'finnova.fetch-ist-products.SUCCESS',
    FETCH_IST_PRODUCTS_FAILURE = 'finnova.fetch-ist-products.FAILURE',

    FETCH_IST_CARDS = 'finnova.fetch-ist-cards',
    FETCH_IST_CARDS_SUCCESS = 'finnova.fetch-ist-cards.SUCCESS',
    FETCH_IST_CARDS_FAILURE = 'finnova.fetch-ist-cards.FAILURE',

    FETCH_SOLL_PRODUCTS = 'finnova.fetch-soll-products',
    FETCH_SOLL_PRODUCTS_SUCCESS = 'finnova.fetch-soll-products.SUCCESS',
    FETCH_SOLL_PRODUCTS_FAILURE = 'finnova.fetch-soll-products.FAILURE',

    FETCH_POWER_OF_ATTORNEY = 'finnova.fetch-power-of-attorney',
    FETCH_POWER_OF_ATTORNEY_SUCCESS = 'finnova.fetch-power-of-attorney.SUCCESS',
    FETCH_POWER_OF_ATTORNEY_FAILURE = 'finnova.fetch-power-of-attorney.FAILURE',

    FETCH_EBANKING_CONTRACTS = 'finnova.fetch-ebanking-contracts',
    FETCH_EBANKING_CONTRACTS_SUCCESS = 'finnova.fetch-ebanking-contracts.SUCCESS',
    FETCH_EBANKING_CONTRACTS_FAILURE = 'finnova.fetch-ebanking-contracts.FAILURE',

    FETCH_ADDRESSES = 'finnova.fetch-addresses',
    FETCH_ADDRESSES_SUCCESS = 'finnova.fetch-addresses.SUCCESS',
    FETCH_ADDRESSES_FAILURE = 'finnova.fetch-addresses.FAILURE',

    FETCH_COMMUNICATION_ENTRIES = 'finnova.fetch-communication-entries',
    FETCH_COMMUNICATION_ENTRIES_SUCCESS = 'finnova.fetch-communication-entries.SUCCESS',
    FETCH_COMMUNICATION_ENTRIES_FAILURE = 'finnova.fetch-communication-entries.FAILURE',

    FETCH_KONTODEPOTS = 'finnova.fetch-kontodepots',
    FETCH_KONTODEPOTS_SUCCESS = 'finnova.fetch-kontodepots.SUCCESS',
    FETCH_KONTODEPOTS_FAILURE = 'finnova.fetch-kontodepots.FAILURE',

    FETCH_CODES = 'finnova.fetch-codes',
    FETCH_CODES_SUCCESS = 'finnova.fetch-codes.success',
    FETCH_CODES_FAILURE = 'finnova.fetch-codes.failure',
}

export const fetchCodes = createAction<FinnovaCodeColumn>(FinnovaApiActions.FETCH_CODES);
export const fetchCodesSuccess = createAction<{ columnName: FinnovaCodeColumn; codes: IFinnovaCode[] }>(FinnovaApiActions.FETCH_CODES_SUCCESS);
export const fetchCodesFailure = createAction(FinnovaApiActions.FETCH_CODES_FAILURE);

export const fetchKannProducts = createAction<string[] | undefined>(FinnovaApiActions.FETCH_KANN_PRODUCTS);
export const fetchKannProductsSuccess = createAction<IProduct[]>(FinnovaApiActions.FETCH_KANN_PRODUCTS_SUCCESS);
export const fetchKannProductsFailure = createAction(FinnovaApiActions.FETCH_KANN_PRODUCTS_FAILURE);

// fetches card products the same way as kann products, but in a separate store section so it does not influence the normal flow
// needed to display card types when ordering products with cards
export const fetchCardProducts = createAction(FinnovaApiActions.FETCH_CARD_PRODUCTS);
export const fetchCardProductsSuccess = createAction<IProduct[]>(FinnovaApiActions.FETCH_CARD_PRODUCTS_SUCCESS);
export const fetchCardProductsFailure = createAction(FinnovaApiActions.FETCH_CARD_PRODUCTS_FAILURE);

export const fetchSollProducts = createAction<string[] | undefined>(FinnovaApiActions.FETCH_SOLL_PRODUCTS);
export const fetchSollProductsSuccess = createAction<IProduct[]>(FinnovaApiActions.FETCH_SOLL_PRODUCTS_SUCCESS);
export const fetchSollProductsFailure = createAction<unknown>(FinnovaApiActions.FETCH_SOLL_PRODUCTS_FAILURE);

export const fetchIstProducts = createAction<string[] | undefined>(FinnovaApiActions.FETCH_IST_PRODUCTS);
export const fetchIstProductsSuccess = createAction<IIstProduct[]>(FinnovaApiActions.FETCH_IST_PRODUCTS_SUCCESS);
export const fetchIstProductsFailure = createAction(FinnovaApiActions.FETCH_IST_PRODUCTS_FAILURE);

export const fetchIstCards = createAction(FinnovaApiActions.FETCH_IST_CARDS);
export const fetchIstCardsSuccess = createAction<IIstKarte[]>(FinnovaApiActions.FETCH_IST_CARDS_SUCCESS);
export const fetchIstCardsFailure = createAction(FinnovaApiActions.FETCH_IST_CARDS_FAILURE);

export const fetchEBankingContracts = createAction(FinnovaApiActions.FETCH_EBANKING_CONTRACTS);
export const fetchEBankingContractsSuccess = createAction<IEBankingContract[]>(FinnovaApiActions.FETCH_EBANKING_CONTRACTS_SUCCESS);
export const fetchEBankingContractsFailure = createAction(FinnovaApiActions.FETCH_EBANKING_CONTRACTS_FAILURE);

export const fetchPowerOfAttorney = createAction<ICustomerBase | undefined>(FinnovaApiActions.FETCH_POWER_OF_ATTORNEY);
export const fetchPowerOfAttorneySuccess = createAction<IPowerOfAttorney[]>(FinnovaApiActions.FETCH_POWER_OF_ATTORNEY_SUCCESS);
export const fetchPowerOfAttorneyFailure = createAction(FinnovaApiActions.FETCH_POWER_OF_ATTORNEY_FAILURE);

export const fetchAddresses = createAction(FinnovaApiActions.FETCH_ADDRESSES);
export const fetchAddressesSuccess = createAction<IAddress[]>(FinnovaApiActions.FETCH_ADDRESSES_SUCCESS);
export const fetchAddressesFailure = createAction(FinnovaApiActions.FETCH_ADDRESSES_FAILURE);

export const fetchKontodepots = createAction(FinnovaApiActions.FETCH_KONTODEPOTS);
export const fetchKontodepotsSuccess = createAction<IKontodepot[]>(FinnovaApiActions.FETCH_KONTODEPOTS_SUCCESS);
export const fetchKontodepotsFailure = createAction(FinnovaApiActions.FETCH_KONTODEPOTS_FAILURE);

export const fetchCommunicationEntries = createAction(FinnovaApiActions.FETCH_COMMUNICATION_ENTRIES);
export const fetchCommunicationEntriesSuccess = createAction<ICommunicationEntries>(FinnovaApiActions.FETCH_COMMUNICATION_ENTRIES_SUCCESS);
export const fetchCommunicationEntriesFailure = createAction(FinnovaApiActions.FETCH_COMMUNICATION_ENTRIES_FAILURE);
